/**
 * storefront-collage-blog.js
 * Place this file in: assets/storefront-collage-blog.js
 *
 * Fetches latest articles from all Shopify blogs via Storefront API
 * and renders them into a split collage grid with a vertical slider.
 * Automatically falls back to content text if excerpt is missing.
 */

(function () {
  'use strict';

  // GraphQL Query to fetch metadata along with full content fallback
  const QUERY = `
    query GetLatestArticles($first: Int!) {
      articles(first: $first, sortKey: PUBLISHED_AT, reverse: true) {
        edges {
          node {
            id
            title
            handle
            excerpt
            contentHtml
            publishedAt
            onlineStoreUrl
            image {
              url
              altText
            }
            author {
              name
            }
            blog {
              title
              handle
            }
            descriptionMetafield: metafield(namespace: "custom", key: "articles_descriptions") {
              value
              type
            }
          }
        }
      }
    }
  `;

  /**
   * Convert a Shopify rich text metafield's JSON value into plain text.
   */
  function richTextToPlainText(rawValue) {
    if (!rawValue) return '';
    try {
      const root = JSON.parse(rawValue);
      const walk = (node) => {
        if (!node) return '';
        if (node.type === 'text') return node.value || '';
        if (Array.isArray(node.children)) return node.children.map(walk).join('');
        return '';
      };
      const blocks = Array.isArray(root.children) ? root.children : [];
      return blocks.map(walk).join(' ').replace(/\s+/g, ' ').trim();
    } catch {
      return '';
    }
  }

  /**
   * Truncate plain text to a maximum number of characters.
   */
  function truncateChars(str, maxChars) {
    if (str.length <= maxChars) return str;
    return str.substring(0, maxChars).trim() + '...';
  }

  /**
   * Format an ISO date string into a human-readable form.
   */
  function formatDate(iso) {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  }

  /**
   * Safely create a text node — never evaluates HTML.
   */
  function text(str) {
    return document.createTextNode(str || '');
  }

  /**
   * Create an element with optional attributes and children.
   */
  function el(tag, attrs, ...children) {
    const node = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (v !== null && v !== undefined) {
          node.setAttribute(k, v);
        }
      });
    }
    children.forEach((child) => {
      if (child == null) return;
      node.appendChild(typeof child === 'string' ? text(child) : child);
    });
    return node;
  }

  /**
   * Build the SVG placeholder shown when an article has no image.
   */
  function buildImagePlaceholder() {
    const wrap = el('div', { class: 'hsb-card__image-placeholder' });
    wrap.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" ' +
      'stroke="currentColor" aria-hidden="true" style="width: 40px; opacity: 0.5;">' +
      '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" ' +
      'd="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 ' +
      '012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 ' +
      '00-2 2v12a2 2 0 002 2z"/>' +
      '</svg>';
    return wrap;
  }

  /**
   * Build the large featured article card (Left Side).
   */
  function buildFeaturedCard(article, settings) {
    const url = article.onlineStoreUrl || '#';
    const card = el('div', { class: 'article-item article-item--featured' });

    // Image Container
    const imgContainer = el('a', { href: url, class: 'article-item__image-container' });
    if (article.image && article.image.url) {
      const img = el('img', {
        src: article.image.url,
        alt: article.image.altText || article.title || '',
        loading: 'lazy',
        style: 'width:100%; height:auto; object-fit:cover;'
      });
      imgContainer.appendChild(img);
    } else {
      imgContainer.appendChild(buildImagePlaceholder());
    }
    card.appendChild(imgContainer);

    // Info Container
    const info = el('div', { class: 'article-item__info' });
    
    // Meta (Category & Date)
    const meta = el('div', { class: 'date-container' });
    if (settings.showCategory && article.blog && article.blog.title) {
      meta.appendChild(el('span', { class: 'article-item__category heading heading--xsmall', style: 'text-transform:uppercase;' }, article.blog.title));
    }
    if (settings.showDate && article.publishedAt) {
      meta.appendChild(el('span', { class: 'article-item__date' }, formatDate(article.publishedAt)));
    }
    if (meta.hasChildNodes()) info.appendChild(meta);

    // Title
    const title = el('h3', { class: 'article-item__title heading h3' });
    title.appendChild(el('a', { href: url }, article.title));
    info.appendChild(title);

    // Metafield Description (custom.articles_descriptions)
    const metaDescription = truncateChars(richTextToPlainText(article.descriptionMetafield && article.descriptionMetafield.value), 200);
    if (metaDescription) {
      info.appendChild(el('div', {
        class: 'article-item__meta-description',
        style: 'font-size:14px; margin-top:8px; line-height:1.5; color:#333;'
      }, metaDescription));
    }

    // Dynamic Excerpt / Short Description (Featured Card)
    // if (settings.showExcerpt) {
    //   const rawExcerpt = article.excerpt || article.contentHtml || '';
    //   // | strip_html ka JavaScript alternative (HTML tags remove karne ke liye)
    //   const cleanExcerpt = rawExcerpt.replace(/<\/?[^>]+(>|$)/g, "").trim(); 
      
    //   if (cleanExcerpt) {
    //     // | truncate: 220 ka JavaScript alternative
    //     const shortDescription = cleanExcerpt.length > 220 ? cleanExcerpt.substring(0, 220) + '...' : cleanExcerpt;
    //     info.appendChild(el('div', { 
    //       class: 'article-item__excerpt rte_content_excerpt', 
    //       style: 'font-size:14px; margin-top:10px; line-height:1.5; color:#555;' 
    //     }, shortDescription));
    //   }
    // }

    card.appendChild(info);
    return card;
  }

  /**
   * Build a horizontal small article card for the Slider (Right Side).
   */
  function buildSliderCard(article, settings) {
    const url = article.onlineStoreUrl || '#';
    const card = el('div', { class: 'article-item article-item--horizontal' });
    
    const flexWrap = el('div', { style: 'display:flex; gap:15px; align-items:center; height:100%;' });

    // Thumbnail Link
    const thumbLink = el('a', { href: url, style: 'width:42%; flex-shrink:0; display:block; margin-inline-end: 0;', class: 'article-item__image-container' });
    if (article.image && article.image.url) {
      const img = el('img', {
        src: article.image.url,
        alt: article.image.altText || article.title || '',
        loading: 'lazy',
        // style: 'width:100%; height:; object-fit:cover; border-radius:4px;'
      });
      thumbLink.appendChild(img);
    } else {
      thumbLink.appendChild(buildImagePlaceholder());
    }
    flexWrap.appendChild(thumbLink);

    // Info Right Content
    const info = el('div', { class: 'article-item__info', style: 'flex-grow:1;' });
    
    // Meta row
    const meta = el('div', { class: 'date-container', style: 'margin-bottom:2px;' });
    if (settings.showCategory && article.blog && article.blog.title) {
      meta.appendChild(el('span', { style: 'text-transform:uppercase; font-size:12px; font-weight:bold; text-decoration:underline; text-decoration-color:#005A84;' }, article.blog.title));
    }
    if (settings.showDate && article.publishedAt) {
      meta.appendChild(el('span', { style: 'font-size:12px;' }, formatDate(article.publishedAt)));
    }
    if (meta.hasChildNodes()) info.appendChild(meta);

    // Title
    const title = el('h5', { class: 'article-item__title heading h5', style: 'font-size:16px; margin:0; line-height:1.4;' });
    title.appendChild(el('a', { href: url, style: 'color:inherit; text-decoration:none;' }, article.title));
    info.appendChild(title);

    // Metafield Description (custom.articles_descriptions)
    const metaDescription = truncateChars(richTextToPlainText(article.descriptionMetafield && article.descriptionMetafield.value), 80);
    if (metaDescription) {
      info.appendChild(el('p', {
        class: 'article-item__meta-description',
        style: 'font-size:12px; margin-top:4px; line-height:1.4; color:#444;'
      }, metaDescription));
    }

    // Dynamic Excerpt / Short Description (Slider Items)
    // if (settings.showExcerpt) {
    //   const rawExcerpt = article.excerpt || article.contentHtml || '';
    //   // | strip_html ka JavaScript alternative
    //   const cleanExcerpt = rawExcerpt.replace(/<\/?[^>]+(>|$)/g, "").trim();
      
    //   if (cleanExcerpt) {
    //     // | truncate: 220 ka JavaScript alternative
    //     const truncated = cleanExcerpt.length > 220 ? cleanExcerpt.substring(0, 80) + '...' : cleanExcerpt;
    //     info.appendChild(el('p', { 
    //       class: 'rte_content_excerpt',
    //       style: 'font-size:12px; margin-top:4px; line-height:1.4; color:#666;' 
    //     }, truncated));
    //   }
    // }

    flexWrap.appendChild(info);
    card.appendChild(flexWrap);
    return card;
  }

  /**
   * Show a loading state.
   */
  function showLoading(container) {
    container.innerHTML = '';
    const wrap = el('div', { style: 'text-align:center; padding: 40px 0;' });
    wrap.appendChild(el('p', null, 'Loading articles…'));
    container.appendChild(wrap);
  }

  /**
   * Show an error state.
   */
  function showError(container, message) {
    container.innerHTML = '';
    container.appendChild(el('div', { style: 'text-align:center; color:red; padding:20px;' }, message));
  }

  /**
   * Safely initialize Slick Vertical/Horizontal Slider.
   */
  function initSlickSlider() {
    if (typeof $ !== 'undefined' && $.fn.slick) {
      $('.article-list__secondary-list.enable-slider').not('.slick-initialized').slick({
        slidesToShow: 3,
        slidesToScroll: 1,
        vertical: true,
        verticalSwiping: true,
        arrows: false,
        dots: false,
        infinite: true,
        autoplay: true,
        autoplaySpeed: 1500,
        speed: 400,
        pauseOnHover: true,
        pauseOnFocus: true,
        adaptiveHeight: false,
        responsive: [
          {
            breakpoint: 767,
            settings: {
              vertical: false,
              verticalSwiping: false,
              slidesToShow: 1.2,
              slidesToScroll: 1,
              infinite: false
            }
          }
        ]
      });
    } else {
      // Retry if jQuery/Slick hook is loading slowly
      setTimeout(initSlickSlider, 200);
    }
  }

  /**
   * Fetch elements and build full split HTML design.
   */
  async function init(root) {
    const dataset = root.dataset;
    const storeDomain = (dataset.storeDomain || '').trim().replace(/\/$/, '');
    const token = (dataset.storefrontToken || '').trim();
    const container = root.querySelector('[data-hsb-container]');

    const settings = {
      showExcerpt: dataset.showExcerpt === 'true',
      showCategory: dataset.showCategory === 'true',
      showDate: dataset.showDate === 'true',
    };

    if (!container) return;
    if (!storeDomain || !token) {
      showError(container, 'Configuration error: Store domain and Storefront API token are required.');
      return;
    }

    showLoading(container);
    const endpoint = `https://${storeDomain}/api/2025-01/graphql.json`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: QUERY,
          variables: { first: 15 }, // Pulling maximum 15 items for smooth loop cycling
        }),
      });

      const data = await response.json();
      const edges = data?.data?.articles?.edges || [];

      if (!edges.length) {
        container.innerHTML = '<p style="text-align:center;padding:20px;">No articles found.</p>';
        return;
      }

      container.innerHTML = '';
      const collageGrid = el('div', { class: 'hsb-container-collage article-list article-list--section article-list--collage' });

      // 1. Render Left Big Card
      collageGrid.appendChild(buildFeaturedCard(edges[0].node, settings));

      // 2. Render Right Side Slider Cards
      const sliderWrapper = el('div', { class: 'article-list__secondary-list enable-slider' });
      for (let i = 1; i < edges.length; i++) {
        sliderWrapper.appendChild(buildSliderCard(edges[i].node, settings));
      }
      
      collageGrid.appendChild(sliderWrapper);
      container.appendChild(collageGrid);

      // 3. Fire Up Slider
      initSlickSlider();

    } catch (err) {
      console.error('[home-storefront-blogs] Process error:', err);
      showError(container, 'Unable to load articles right now.');
    }
  }

  // Bind execution listener
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-hsb-root]').forEach((root) => init(root));
  });
})();